import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  LibraryAdd as AddLocationGroupIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import UserAvatar from "../components/common/UserAvatar";
import LogislyOrderTakerV2Route from "../components/logislySpxOnCall/LogislyOrderTakerV2Route";
import { LogislyOrderTakerConfigsV2HistorySection } from "./LogislyOrderTakerConfigsV2HistoryPage";
import type { RootState } from "../store";
import { isUserAdmin, UserRole } from "../types/user";
import type { LogislyDriver, LogislyLocation, LogislyTruck } from "../types/logislyOrderTaker";
import type { LogislyOrderTakerConfigV2 } from "../types/logislyOrderTakerV2";
import type { LogislyLocationGroup } from "../types/logislyLocationGroup";
import { getLogislyOrderTakerV2TruckChipStyle } from "../utils/logislyOrderTakerV2";
import {
  getLogislyDriversApi,
  getLogislyLocationsApi,
  getLogislyTrucksApi,
} from "../api/logislyOrderTakerApi";
import {
  cancelLogislyOrderTakerV2ConfigApi,
  createLogislyOrderTakerV2ConfigApi,
  getLogislyOrderTakerV2OpenConfigsApi,
  moveLogislyOrderTakerV2PriorityApi,
  type LogislyOrderTakerV2Payload,
} from "../api/logislyOrderTakerV2Api";
import { getLogislyLocationGroupsApi } from "../api/logislyLocationGroupApi";
import { formatLicensePlateInput } from "../utils/formatLicensePlate";

const isLinked = (item: { linkStatus?: string }) => item.linkStatus === "LINKED";
const locationSort = (a: LogislyLocation, b: LogislyLocation) => a.name.localeCompare(b.name);
const truckLabel = (truck: LogislyTruck) =>
  truck.number ? formatLicensePlateInput(truck.number) : truck.logislyTruckId;
const driverLabel = (driver: LogislyDriver) =>
  driver.name || driver.phone || driver.logislyDriverId;
const formatCreatedAt = (value?: string) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return value;
  }
};
type DropPosition = "before" | "after";

const getConfigCopyPayload = (config: LogislyOrderTakerConfigV2): LogislyOrderTakerV2Payload => ({
  logislyTruckId: config.logislyTruckId,
  logislyDriverId: config.logislyDriverId,
  muat: { id: config.muat.logislyLocationId },
  bongkar: [...config.bongkar]
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((location, index) => ({
      id: location.logislyLocationId,
      priority: (index + 1) * 10,
    })),
  isMultidrop: config.isMultidrop,
});

const hasSameCopyValues = (
  payload: LogislyOrderTakerV2Payload,
  truck: LogislyTruck | null,
  driver: LogislyDriver | null,
  muat: LogislyLocation | null,
  bongkar: LogislyLocation[],
  isMultidrop: boolean,
) =>
  truck?.logislyTruckId === payload.logislyTruckId &&
  driver?.logislyDriverId === payload.logislyDriverId &&
  muat?.logislyLocationId === payload.muat.id &&
  isMultidrop === payload.isMultidrop &&
  bongkar.length === payload.bongkar.length &&
  bongkar.every((location, index) => location.logislyLocationId === payload.bongkar[index]?.id);

interface ConfigDialogProps {
  open: boolean;
  trucks: LogislyTruck[];
  drivers: LogislyDriver[];
  locations: LogislyLocation[];
  locationGroups: LogislyLocationGroup[];
  copyPayload: LogislyOrderTakerV2Payload | null;
  onClose: () => void;
  onSubmit: (payload: LogislyOrderTakerV2Payload) => Promise<void>;
}

const ConfigDialog = ({
  open,
  trucks,
  drivers,
  locations,
  locationGroups,
  copyPayload,
  onClose,
  onSubmit,
}: ConfigDialogProps) => {
  const { t } = useTranslation();
  const [truck, setTruck] = useState<LogislyTruck | null>(null);
  const [driver, setDriver] = useState<LogislyDriver | null>(null);
  const [muat, setMuat] = useState<LogislyLocation | null>(null);
  const [bongkar, setBongkar] = useState<LogislyLocation[]>([]);
  const [bongkarInsertionIndex, setBongkarInsertionIndex] = useState<number | null>(null);
  const [draggedBongkarId, setDraggedBongkarId] = useState<string>();
  const [bongkarDropTarget, setBongkarDropTarget] = useState<{
    id: string;
    position: DropPosition;
  }>();
  const bongkarPointerDragRef = useRef<
    { id: string; startX: number; startY: number; moved: boolean } | undefined
  >(undefined);
  const suppressBongkarClickRef = useRef(false);
  const [locationGroupDialogOpen, setLocationGroupDialogOpen] = useState(false);
  const [locationGroupSearch, setLocationGroupSearch] = useState("");
  const [expandedLocationGroupId, setExpandedLocationGroupId] = useState<string>();
  const [isMultidrop, setIsMultidrop] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTruck(
      copyPayload
        ? (trucks.find((item) => item.logislyTruckId === copyPayload.logislyTruckId) ?? null)
        : null,
    );
    setDriver(
      copyPayload
        ? (drivers.find((item) => item.logislyDriverId === copyPayload.logislyDriverId) ?? null)
        : null,
    );
    setMuat(
      copyPayload
        ? (locations.find((item) => item.logislyLocationId === copyPayload.muat.id) ?? null)
        : null,
    );
    setBongkar(
      copyPayload
        ? copyPayload.bongkar
            .map((destination) =>
              locations.find((item) => item.logislyLocationId === destination.id),
            )
            .filter((item): item is LogislyLocation => Boolean(item))
        : [],
    );
    setBongkarInsertionIndex(null);
    setDraggedBongkarId(undefined);
    setBongkarDropTarget(undefined);
    setIsMultidrop(copyPayload?.isMultidrop ?? true);
    setLocationGroupDialogOpen(false);
    setLocationGroupSearch("");
    setExpandedLocationGroupId(undefined);
  }, [copyPayload, drivers, locations, open, trucks]);
  const matchingLocationGroups = useMemo(() => {
    const query = locationGroupSearch.trim().toLocaleLowerCase();
    if (!query) return locationGroups;
    return locationGroups.filter((group) => group.name.toLocaleLowerCase().includes(query));
  }, [locationGroupSearch, locationGroups]);
  const locationNameById = useMemo(
    () => new Map(locations.map((location) => [location.logislyLocationId, location.name])),
    [locations],
  );
  const addLocationGroup = (group: LogislyLocationGroup) => {
    const selectedIds = new Set(bongkar.map((location) => location.logislyLocationId));
    const additions = group.logislyLocationIds.flatMap((id) => {
      const location = locations.find((item) => item.logislyLocationId === id);
      if (
        !location ||
        location.logislyLocationId === muat?.logislyLocationId ||
        selectedIds.has(location.logislyLocationId)
      ) {
        return [];
      }
      selectedIds.add(location.logislyLocationId);
      return [location];
    });
    if (additions.length > 0) {
      const insertionIndex = Math.min(bongkarInsertionIndex ?? bongkar.length, bongkar.length);
      setBongkar([
        ...bongkar.slice(0, insertionIndex),
        ...additions,
        ...bongkar.slice(insertionIndex),
      ]);
      setBongkarInsertionIndex(insertionIndex + additions.length);
    }
    setLocationGroupDialogOpen(false);
  };
  const getBongkarDropTarget = (sourceId: string, clientX: number, clientY: number) => {
    const target = document
      .elementFromPoint(clientX, clientY)
      ?.closest<HTMLElement>("[data-bongkar-id]");
    const targetId = target?.dataset.bongkarId;
    if (!target || !targetId || targetId === sourceId) return undefined;
    const bounds = target.getBoundingClientRect();
    return {
      id: targetId,
      position: clientX < bounds.left + bounds.width / 2 ? "before" : ("after" as DropPosition),
    };
  };
  const reorderBongkar = (sourceId: string, targetId: string, position: DropPosition) => {
    if (sourceId === targetId) return;
    setBongkar((current) => {
      const sourceIndex = current.findIndex((location) => location.logislyLocationId === sourceId);
      const targetIndex = current.findIndex((location) => location.logislyLocationId === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const reordered = [...current];
      const [moved] = reordered.splice(sourceIndex, 1);
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      reordered.splice(adjustedTargetIndex + (position === "after" ? 1 : 0), 0, moved);
      return reordered;
    });
    setDraggedBongkarId(undefined);
    setBongkarDropTarget(undefined);
  };
  const unchangedCopy =
    copyPayload && hasSameCopyValues(copyPayload, truck, driver, muat, bongkar, isMultidrop);
  const canSubmit = Boolean(truck && driver && muat && bongkar.length > 0 && !unchangedCopy);
  const submit = async () => {
    if (!canSubmit || !truck || !driver || !muat || bongkar.length === 0) return;
    setSubmitting(true);
    try {
      await onSubmit({
        logislyTruckId: truck.logislyTruckId,
        logislyDriverId: driver.logislyDriverId,
        muat: { id: muat.logislyLocationId },
        bongkar: bongkar.map((location, index) => ({
          id: location.logislyLocationId,
          priority: (index + 1) * 10,
        })),
        isMultidrop,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {copyPayload ? "Copy Order Taker V2 Config" : "Add Order Taker V2 Config"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Autocomplete
            options={trucks}
            value={truck}
            onChange={(_, value) => setTruck(value)}
            getOptionLabel={truckLabel}
            isOptionEqualToValue={(a, b) => a.logislyTruckId === b.logislyTruckId}
            renderInput={(params) => <TextField {...params} label="Truck" required />}
          />
          <Autocomplete
            options={drivers}
            value={driver}
            onChange={(_, value) => setDriver(value)}
            getOptionLabel={driverLabel}
            isOptionEqualToValue={(a, b) => a.logislyDriverId === b.logislyDriverId}
            renderInput={(params) => <TextField {...params} label="Driver" required />}
          />
          <Autocomplete
            options={locations}
            value={muat}
            onChange={(_, value) => {
              setMuat(value);
              setBongkar((current) =>
                current.filter((item) => item.logislyLocationId !== value?.logislyLocationId),
              );
            }}
            getOptionLabel={(item) => item.name}
            isOptionEqualToValue={(a, b) => a.logislyLocationId === b.logislyLocationId}
            renderInput={(params) => <TextField {...params} label="Muat" required />}
          />
          <Autocomplete
            multiple
            filterSelectedOptions
            options={locations.filter(
              (location) => location.logislyLocationId !== muat?.logislyLocationId,
            )}
            value={bongkar}
            onChange={(_, value, reason, details) => {
              if (reason !== "selectOption" || !details?.option) {
                setBongkar(value);
                setBongkarInsertionIndex(null);
                return;
              }
              const location = details.option;
              const insertionIndex = Math.min(
                bongkarInsertionIndex ?? bongkar.length,
                bongkar.length,
              );
              setBongkar((current) => [
                ...current.slice(0, insertionIndex),
                location,
                ...current.slice(insertionIndex),
              ]);
              setBongkarInsertionIndex(insertionIndex + 1);
            }}
            getOptionLabel={(item) => item.name}
            isOptionEqualToValue={(a, b) => a.logislyLocationId === b.logislyLocationId}
            renderTags={(value, getTagProps) =>
              value.map((location, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                const isDropTarget = bongkarDropTarget?.id === location.logislyLocationId;
                const isInsertionPoint = bongkarInsertionIndex === index + 1;
                return (
                  <Box
                    key={key}
                    component="span"
                    data-bongkar-id={location.logislyLocationId}
                    onPointerDown={(event) => {
                      if (
                        event.button !== 0 ||
                        (event.target as HTMLElement).closest(".MuiChip-deleteIcon")
                      ) {
                        return;
                      }
                      event.currentTarget.setPointerCapture(event.pointerId);
                      bongkarPointerDragRef.current = {
                        id: location.logislyLocationId,
                        startX: event.clientX,
                        startY: event.clientY,
                        moved: false,
                      };
                    }}
                    onPointerMove={(event) => {
                      const drag = bongkarPointerDragRef.current;
                      if (!drag) return;
                      if (
                        !drag.moved &&
                        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 6
                      ) {
                        return;
                      }
                      drag.moved = true;
                      event.preventDefault();
                      setDraggedBongkarId(drag.id);
                      setBongkarDropTarget(
                        getBongkarDropTarget(drag.id, event.clientX, event.clientY),
                      );
                    }}
                    onPointerUp={(event) => {
                      const drag = bongkarPointerDragRef.current;
                      bongkarPointerDragRef.current = undefined;
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      }
                      if (drag?.moved) {
                        const target = getBongkarDropTarget(drag.id, event.clientX, event.clientY);
                        if (target) reorderBongkar(drag.id, target.id, target.position);
                        suppressBongkarClickRef.current = true;
                      }
                      setDraggedBongkarId(undefined);
                      setBongkarDropTarget(undefined);
                    }}
                    onPointerCancel={() => {
                      bongkarPointerDragRef.current = undefined;
                      setDraggedBongkarId(undefined);
                      setBongkarDropTarget(undefined);
                    }}
                    sx={{
                      display: "inline-flex",
                      cursor: draggedBongkarId === location.logislyLocationId ? "grabbing" : "grab",
                      touchAction: "none",
                    }}
                  >
                    <Chip
                      {...tagProps}
                      icon={<DragIcon fontSize="small" />}
                      label={location.name}
                      onClick={() => {
                        if (suppressBongkarClickRef.current) {
                          suppressBongkarClickRef.current = false;
                          return;
                        }
                        setBongkarInsertionIndex(index + 1);
                      }}
                      title="Drag to reorder. Click to insert new destinations after this one."
                      sx={{
                        ...(isInsertionPoint ? { outline: 2, outlineColor: "primary.main" } : {}),
                        ...(isDropTarget
                          ? bongkarDropTarget.position === "before"
                            ? { borderLeft: 3, borderColor: "primary.main" }
                            : { borderRight: 3, borderColor: "primary.main" }
                          : {}),
                      }}
                    />
                  </Box>
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Bongkar alternatives" required />
            )}
          />
          <Button
            size="small"
            startIcon={<AddLocationGroupIcon />}
            onClick={() => {
              setLocationGroupSearch("");
              setExpandedLocationGroupId(undefined);
              setLocationGroupDialogOpen(true);
            }}
            disabled={locationGroups.length === 0}
            sx={{ alignSelf: "flex-start", textTransform: "none", mt: -0.5 }}
          >
            Use a location group
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1.25 }}>
            {t(
              "Each selected destination is an OR alternative; drag to reorder, or click one to insert new locations after it.",
            )}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isMultidrop}
                onChange={(event) => setIsMultidrop(event.target.checked)}
              />
            }
            label={t("Allow multidrop order matching")}
          />
          {unchangedCopy && (
            <Typography variant="body2" color="text.secondary">
              Change at least one field before saving this copy.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit || submitting}>
          {copyPayload ? "Create copy" : "Add"}
        </Button>
      </DialogActions>
      <Dialog
        open={locationGroupDialogOpen}
        onClose={() => setLocationGroupDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Choose a location group</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, overflow: "hidden" }}
        >
          <TextField
            autoFocus
            size="small"
            placeholder="Search groups"
            value={locationGroupSearch}
            onChange={(event) => setLocationGroupSearch(event.target.value)}
          />
          <Box sx={{ maxHeight: 280, overflowY: "auto", mx: -1 }}>
            {matchingLocationGroups.length > 0 ? (
              <List disablePadding>
                {matchingLocationGroups.map((group) => {
                  const expanded = expandedLocationGroupId === group._id;
                  return (
                    <Box key={group._id} sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ListItemButton
                          dense
                          onClick={() =>
                            setExpandedLocationGroupId(expanded ? undefined : group._id)
                          }
                          aria-expanded={expanded}
                          sx={{ py: 0.5, minHeight: 40, flex: 1 }}
                        >
                          <ListItemText
                            primary={group.name}
                            secondary={`${group.logislyLocationIds.length} locations`}
                            slotProps={{
                              primary: { variant: "body2", noWrap: true },
                              secondary: { variant: "caption" },
                            }}
                          />
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
                          />
                        </ListItemButton>
                        <Button
                          size="small"
                          onClick={() => addLocationGroup(group)}
                          sx={{ mr: 0.5, textTransform: "none" }}
                        >
                          Add
                        </Button>
                      </Box>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ maxHeight: 120, overflowY: "auto", px: 2, pb: 1.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            {group.logislyLocationIds
                              .map((id) => locationNameById.get(id) ?? `Missing location (${id})`)
                              .join(" · ")}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ px: 1, py: 2 }}>
                No matching location groups.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationGroupDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const LogislyOrderTakerConfigsV2OpenPage = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const canManage = isUserAdmin(user) || user?.role === UserRole.STAFF;
  const [configs, setConfigs] = useState<LogislyOrderTakerConfigV2[]>([]);
  const [trucks, setTrucks] = useState<LogislyTruck[]>([]);
  const [drivers, setDrivers] = useState<LogislyDriver[]>([]);
  const [locations, setLocations] = useState<LogislyLocation[]>([]);
  const [locationGroups, setLocationGroups] = useState<LogislyLocationGroup[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyPayload, setCopyPayload] = useState<LogislyOrderTakerV2Payload | null>(null);
  const [draggedId, setDraggedId] = useState<string>();
  const [dropTarget, setDropTarget] = useState<{
    id: string;
    position: DropPosition;
  }>();
  const load = useCallback(async () => {
    const [configData, truckData, driverData, locationData, locationGroupData] = await Promise.all([
      getLogislyOrderTakerV2OpenConfigsApi(),
      getLogislyTrucksApi(true),
      getLogislyDriversApi(true),
      getLogislyLocationsApi(true),
      getLogislyLocationGroupsApi(),
    ]);
    setConfigs(configData);
    setTrucks(truckData);
    setDrivers(driverData);
    setLocations(locationData);
    setLocationGroups(locationGroupData);
  }, []);
  useEffect(() => {
    load().catch((error) => console.error("Failed to load Logisly V2 configs", error));
  }, [load]);
  const linkedTrucks = useMemo(() => trucks.filter(isLinked), [trucks]);
  const linkedDrivers = useMemo(() => drivers.filter(isLinked), [drivers]);
  const linkedLocations = useMemo(
    () => [...locations.filter(isLinked)].sort(locationSort),
    [locations],
  );
  const submit = async (payload: LogislyOrderTakerV2Payload) => {
    await createLogislyOrderTakerV2ConfigApi(payload);
    await load();
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setCopyPayload(null);
  };
  const cancel = async (id: string) => {
    await cancelLogislyOrderTakerV2ConfigApi(id);
    await load();
  };
  const drop = async (targetId: string, position: DropPosition) => {
    if (!draggedId || draggedId === targetId) return;
    try {
      const payload =
        position === "before" ? { beforeConfigId: targetId } : { afterConfigId: targetId };
      setConfigs(await moveLogislyOrderTakerV2PriorityApi(draggedId, payload));
    } finally {
      setDraggedId(undefined);
      setDropTarget(undefined);
    }
  };
  const updateDropTarget = (targetId: string, event: DragEvent<HTMLTableRowElement>) => {
    if (!draggedId || draggedId === targetId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
    setDropTarget((current) =>
      current?.id === targetId && current.position === position
        ? current
        : { id: targetId, position },
    );
  };
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Order Taker Configs V2
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active order-taking queue. Drag the handle to set matching priority.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canManage}
          onClick={() => {
            setCopyPayload(null);
            setDialogOpen(true);
          }}
        >
          Add config
        </Button>
      </Box>
      <ConfigDialog
        open={dialogOpen}
        trucks={linkedTrucks}
        drivers={linkedDrivers}
        locations={linkedLocations}
        locationGroups={locationGroups}
        copyPayload={copyPayload}
        onClose={closeDialog}
        onSubmit={submit}
      />
      {configs.length === 0 ? (
        <Typography color="text.secondary">
          There are no open configs. Add one to create an active order-taking rule.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 28, px: 0.5 }} />
              <TableCell>ID</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Truck</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow
                key={config._id}
                onDragOver={(event) => updateDropTarget(config._id, event)}
                onDrop={() => {
                  if (dropTarget?.id) drop(dropTarget.id, dropTarget.position);
                }}
                sx={{
                  "& > td": {
                    ...(draggedId === config._id ? { backgroundColor: "action.selected" } : {}),
                    ...(dropTarget?.id === config._id
                      ? dropTarget.position === "before"
                        ? { borderTop: "3px solid #FBC02D" }
                        : { borderBottom: "3px solid #FBC02D" }
                      : {}),
                    transition: "background-color 120ms ease-in-out",
                  },
                }}
              >
                <TableCell sx={{ width: 28, px: 0.5 }}>
                  <Box
                    component="span"
                    draggable={canManage}
                    onDragStart={() => {
                      setDraggedId(config._id);
                      setDropTarget(undefined);
                    }}
                    onDragEnd={() => {
                      setDraggedId(undefined);
                      setDropTarget(undefined);
                    }}
                    aria-label="Drag to reorder config"
                    sx={{ display: "inline-flex", cursor: canManage ? "grab" : "default" }}
                  >
                    <DragIcon fontSize="small" color="action" />
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{config.orderTakerConfigId}</TableCell>
                <TableCell>
                  <LogislyOrderTakerV2Route config={config} />
                  {config.linkStatus !== "LINKED" && (
                    <Chip
                      size="small"
                      label="Link warning"
                      color="warning"
                      sx={{ mt: 0.5, ml: 0.5 }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Chip
                    size="small"
                    label={
                      config.truck?.logisly?.number
                        ? formatLicensePlateInput(config.truck.logisly.number)
                        : config.logislyTruckId
                    }
                    sx={getLogislyOrderTakerV2TruckChipStyle(
                      config.truck?.logisly?.number ?? config.logislyTruckId,
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {config.driver?.logisly?.name || config.logislyDriverId}
                  </Typography>
                  {config.driver?.logisly?.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {config.driver.logisly.phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <UserAvatar
                    user={
                      config.createdBy || {
                        _id: config.createdByUserId,
                        name: config.createdByUserId,
                      }
                    }
                    tooltipText={
                      config.createdBy?.username || config.createdBy?.name || config.createdByUserId
                    }
                    size={28}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Chip size="small" label="Active" color="success" />
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {formatCreatedAt(config.createdAt)}
                  </Typography>
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        aria-label="Copy config"
                        onClick={() => {
                          setCopyPayload(getConfigCopyPayload(config));
                          setDialogOpen(true);
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Cancel config"
                        onClick={() => cancel(config._id)}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Divider sx={{ my: 4 }} />
      <LogislyOrderTakerConfigsV2HistorySection embedded />
    </Box>
  );
};

export default LogislyOrderTakerConfigsV2OpenPage;
